'use client'

import { useState } from 'react'
import Link from 'next/link'
import { cardLogoFallbackClass } from '@/lib/service-tool-logos'

export default function RelatedContentStripCard({
  href,
  title,
  description,
  imageSrc,
  fallbackLabel,
  compact = false,
  /** Sidebar: row inside one shared list card (no per-item outer border). */
  nestedRow = false,
}: {
  href: string
  title: string
  description: string | null
  imageSrc: string | null
  fallbackLabel: string
  compact?: boolean
  nestedRow?: boolean
}) {
  const [imgError, setImgError] = useState(false)
  const showImg = Boolean(imageSrc && !imgError)
  const initial = fallbackLabel.trim().charAt(0).toUpperCase() || '?'

  const thumb = compact ? 'h-9 w-9' : 'h-12 w-12'
  const imgSize = compact ? 36 : 48
  const descSize = compact ? 'text-xs' : 'text-sm'
  const initialCls = compact ? 'text-xs' : 'text-sm'

  const rowClasses = nestedRow
    ? 'gap-2.5 border-0 bg-transparent px-3 py-2.5 transition-colors hover:bg-slate-50'
    : compact
      ? 'gap-2.5 rounded-lg border border-slate-200 bg-white p-2.5 transition-colors hover:border-blue-300 hover:bg-slate-50'
      : 'gap-3 rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-blue-300 hover:bg-slate-50'

  return (
    <Link href={href} className={`group flex ${rowClasses}`}>
      <div className={`${thumb} shrink-0`}>
        {showImg ? (
          <img
            src={imageSrc!}
            alt=""
            width={imgSize}
            height={imgSize}
            className={`${thumb} rounded-lg border border-slate-100 bg-white object-contain p-1 shadow-sm`}
            onError={() => setImgError(true)}
          />
        ) : (
          <div
            className={`flex ${thumb} items-center justify-center rounded-lg border border-slate-100 ${initialCls} font-bold shadow-sm ${cardLogoFallbackClass(fallbackLabel)}`}
            aria-hidden
          >
            {initial}
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <span className={`font-medium text-slate-900 group-hover:text-blue-700 ${compact ? 'text-sm' : 'text-base'}`}>{title}</span>
        {description ? (
          <p className={`mt-0.5 ${descSize} text-slate-500 line-clamp-2`}>{description}</p>
        ) : null}
      </div>
    </Link>
  )
}
